import { useLanguage } from "@/contexts/LanguageContext";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import { useState, useRef, useEffect } from "react";
import { prefetchTranslations } from "@/lib/translatePrefetch";
import type { TranslateState } from "@/lib/translateTelemetry";

const LanguageSelector = () => {
  const { currentLang, setCurrentLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === currentLang);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Lock the selector while a translation pass is streaming.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { state: TranslateState };
      setBusy(detail.state === "loading");
    };
    window.addEventListener("translate:state", handler as EventListener);
    return () => window.removeEventListener("translate:state", handler as EventListener);
  }, []);

  const handleSelect = (code: string) => {
    if (busy) return;
    setCurrentLang(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-notranslate="true" translate="no">
      <button
        onClick={() => !busy && setOpen(!open)}
        disabled={busy}
        aria-busy={busy}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-body text-foreground transition-colors ${
          busy ? "opacity-60 cursor-wait" : "hover:bg-accent/10"
        }`}
        aria-label="Select language"
      >
        <span className="text-base leading-none" aria-hidden="true">{current?.flag}</span>
        <span>{current?.native_name || "Deutsch"}</span>
        {busy ? (
          <span
            className="w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin ml-0.5"
            aria-hidden="true"
          />
        ) : (
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      {open && !busy && (
        <div className="absolute top-full right-0 mt-1 w-44 glass-card rounded-lg py-1 shadow-lg z-50">
          {languages.map((lang) => {
            const isCurrent = lang.code === currentLang;
            return (
              <button
                key={lang.code}
                onMouseEnter={() => prefetchTranslations(lang.code)}
                onFocus={() => prefetchTranslations(lang.code)}
                onClick={() => handleSelect(lang.code)}
                disabled={isCurrent}
                className={`w-full text-left px-4 py-2 text-sm font-body transition-colors flex items-center gap-2.5 ${
                  isCurrent
                    ? "text-accent bg-accent/5 cursor-default"
                    : "text-foreground hover:text-accent hover:bg-accent/5"
                }`}
              >
                <span className="text-base leading-none" aria-hidden="true">{lang.flag}</span>
                <span>{lang.native_name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
